import { Card as MuiCard, Tooltip, styled } from '@mui/material';
import { useContextMenu } from '../../contextMenu/context';
import type { StorageUnit } from 'store/storageunits/types';
import { StorageUnitType } from 'store/storageunits/types';
import { useRef } from 'react';
import { useDragAndDrop, useSelection } from '../../context';
import { splitName } from 'util/nameHelper';
import { useIsExternal } from 'auth/useIsExternal';
import { useIsProject } from 'hooks/useIsProject';
import { useSelector } from 'react-redux';
import type { RootState } from 'store';

export const WIDTH = 240;

const CardWrapper = styled(MuiCard)`
  width: ${WIDTH}px;
  box-shadow: 0px 0.6px 4px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  border: 2px solid transparent;

  &[data-istarget='true'] {
    border: 2px solid #1976d2;
  }

  &[data-readonly='true'] {
    background-color: ${({ theme }) => theme.palette.grey[100]};
    color: ${({ theme }) => theme.palette.grey[600]};
  }

  &[data-selected='true'] {
    border: 2px solid #1976d2;
  }
`;

type Props = {
  storageUnit: StorageUnit;
  children: React.ReactNode;
  readOnly?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
} & React.ComponentProps<typeof MuiCard>;

const Card = ({
  storageUnit,
  children,
  readOnly,
  isSelected,
  isDisabled = false,
  ...props
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { dragAndDropProps, dropTarget } = useDragAndDrop(storageUnit);
  const isExternal = useIsExternal();
  const checkIsProject = useIsProject();
  const { folderPath } = useSelector(
    (state: RootState) => state.storageUnitsState,
  );

  const { openContextMenu } = useContextMenu();
  const { selectedIds } = useSelection();

  const { id } = storageUnit;

  const isProject = checkIsProject(storageUnit);
  const isFileInProject =
    storageUnit.type === StorageUnitType.File &&
    folderPath.some((ancestor) => checkIsProject(ancestor));
  const isDraggable =
    !isDisabled &&
    !storageUnit.archived &&
    !(isExternal && (isProject || isFileInProject));

  const onContextMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (readOnly || isDisabled) {
      return;
    }

    if (!ref.current) {
      return;
    }

    openContextMenu(selectedIds.length > 0 ? null : storageUnit, {
      x: e.pageX,
      y: e.pageY,
    });
  };

  return (
    <Tooltip
      title={splitName(storageUnit.type, storageUnit.name)}
      placement='top'
      enterDelay={700}
      arrow
    >
      <CardWrapper
        {...props}
        {...dragAndDropProps}
        ref={ref}
        draggable={isDraggable}
        onContextMenu={onContextMenu}
        data-istarget={id === dropTarget}
        data-readonly={readOnly ? 'true' : undefined}
        data-selected={isSelected ? 'true' : undefined}
      >
        {children}
      </CardWrapper>
    </Tooltip>
  );
};

export default Card;
